import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import HouseIcon from 'apollo-react-icons/House';
import MedicalCard from 'apollo-react-icons/MedicalCard';
import PresentationHierarchyLight from 'apollo-react-icons/PresentationHierarchyLight';
import StatusCheck from 'apollo-react-icons/StatusCheck';
import FileAccountPlan from 'apollo-react-icons/FileAccountPlan';
import ChevronLeft2 from 'apollo-react-icons/ChevronLeft2';
import Planner from 'apollo-react-icons/Planner';
import Bell from 'apollo-react-icons/Bell';
import CreditCardBack from 'apollo-react-icons/CreditCardBack';

import { logout } from '../../../services/authService';
import {
  RESOURCES,
  getAllLogResources,
} from '../../../constants/resourceConstants';
import {
  PRIVILEGE_ACTIONS,
  REQUEST_PRIVILEGES,
} from '../../../constants/privilege.constant';
import { useSidebarCategories } from '../../Admin/Layout/SidebarCategoriesMenu';
import usePermissions from '../../../hooks/features/permissions/usePermissions';

/**
 * Unified hook that builds sidebar menu items based on user permissions.
 * Uses the new permission-based system with three-tier access control:
 * 1. RBAC-Based Modules (require READ privilege)
 * 2. Special Business Logic Modules (custom rules)
 * 3. Always Visible Modules (no permissions required)
 *
 * The role is determined by inspecting the current route prefix:
 *   - /admin/*   -> admin menu items (privilege-based)
 *   - /customer/* -> customer menu items
 *
 * @returns {{ menuItems: Array, isLoading: boolean }}
 */
const useMenuItems = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, ready, i18n } = useTranslation();

  // Force re-render when language changes to update all translations
  useEffect(() => {
    // This effect doesn't need to do anything, just trigger re-render
  }, [i18n.language]);

  const isAdmin = location.pathname.startsWith('/admin');

  // Use the core permission system directly
  const {
    hasPrivilege,
    hasReadPrivilegeOnAny,
    userContext: { isSuper, isExternal, isProfileLoaded },
    isLoading: permissionsLoading,
  } = usePermissions();

  // Create menu-specific convenience functions (following O/C principle)
  const hasReadPrivilege = useCallback(
    (resource, options) =>
      hasPrivilege(resource, PRIVILEGE_ACTIONS.READ, options),
    [hasPrivilege]
  );

  const hasReviewPrivilege = useCallback(
    (resource, options) =>
      hasPrivilege(resource, REQUEST_PRIVILEGES.REVIEW, options),
    [hasPrivilege]
  );

  // Dynamic permission checking functions
  const checkUserRole = useCallback(
    (roleName) => {
      return hasReadPrivilege(roleName);
    },
    [hasReadPrivilege]
  );

  // Helper functions for menu visibility - using core permission hook
  const canAccessUserManagement = hasReadPrivilegeOnAny([
    RESOURCES.ADMIN_USERS,
    RESOURCES.ROLE_GROUPS,
    RESOURCES.EXTERNAL_USERS,
    RESOURCES.EXTERNAL_ORGANIZATIONS_CATEGORIES,
    RESOURCES.ORG_STRUCTURE, // Include org structure in the standard check
  ]);

  const canAccessFormManagement = hasReadPrivilegeOnAny([
    RESOURCES.FORM,
    RESOURCES.SUBFORM,
  ]);
  const canAccessWorkflows = hasReadPrivilege(RESOURCES.WORKFLOWS);
  // Admin Requests require specific REVIEW privilege - super admin is explicitly excluded
  const canAccessAdminRequests = hasReviewPrivilege(RESOURCES.REQUESTS, {
    excludeSuper: true, // Super admin should never see requests menu
  });
  const canAccessExternalRequests = isExternal;
  const canAccessNotificationsSLA = hasReadPrivilege(
    RESOURCES.NOTIFICATIONS_SLA
  );

  // Dynamic audit logs - automatically includes all log resources from resourceConstants
  const canAccessAuditLogs = hasReadPrivilegeOnAny(getAllLogResources());

  // Individual log resource permissions for menu items
  const canAccessRoleGroupsLogs = hasReadPrivilege(RESOURCES.ROLE_GROUPS_LOGS);
  const canAccessAdminUsersLogs = hasReadPrivilege(RESOURCES.ADMIN_USERS_LOGS);
  const canAccessExternalUsersLogs = hasReadPrivilege(
    RESOURCES.EXTERNAL_USERS_LOGS
  );
  const canAccessExternalCategoriesLogs = hasReadPrivilege(
    RESOURCES.EXTERNAL_ORGANIZATIONS_CATEGORIES_LOGS
  );
  const canAccessSubformLogs = hasReadPrivilege(RESOURCES.SUBFORM_LOGS);
  const canAccessFormLogs = hasReadPrivilege(RESOURCES.FORM_LOGS);
  const canAccessWorkflowLogs = hasReadPrivilege(RESOURCES.WORKFLOWS_LOGS);
  const canAccessNotificationsSLALogs = hasReadPrivilege(
    RESOURCES.NOTIFICATIONS_SLA_LOGS
  );
  const canAccessCommentsLogs = hasReadPrivilege(RESOURCES.COMMENTS_LOGS);
  const canAccessAdditionalDocumentsLogs = hasReadPrivilege(
    RESOURCES.ADDITIONAL_DOCUMENTS_LOGS
  );
  const canAccessOrgStructureLogs = hasReadPrivilege(
    RESOURCES.ORG_STRUCTURE_LOGS
  );

  const { categoriesMenuItems } = useSidebarCategories({
    checkUserRole,
    isProfileLoaded,
    isSuper,
  });

  const signOutItem = useMemo(
    () => ({
      text: t('sign_out'),
      icon: ChevronLeft2,
      href: '/login',
      handleOnClick: () => {
        logout();
        navigate('/login');
      },
    }),
    [t, navigate]
  );

  const buildAdminMenuItems = useCallback(
    () => [
      // Dashboard - Always visible
      {
        text: t('dashboard'),
        icon: HouseIcon,
        handleOnClick: () => {
          navigate('/admin/home');
        },
        pathname: '/admin/home',
      },

      // User Management - RBAC + Special Logic
      ...(canAccessUserManagement
        ? [
            {
              text: t('user_management'),
              icon: MedicalCard,
              menuKey: 'user_management',
              menuItems: [
                ...(hasReadPrivilege(RESOURCES.ROLE_GROUPS)
                  ? [
                      {
                        text: t('role_groups'),
                        handleOnClick: () => {
                          navigate('/admin/roles');
                        },
                        pathname: '/admin/roles',
                      },
                    ]
                  : []),
                ...(hasReadPrivilege(RESOURCES.ADMIN_USERS)
                  ? [
                      {
                        text: t('internal_users'),
                        handleOnClick: () => {
                          navigate('/admin/users');
                        },
                        pathname: '/admin/users',
                      },
                    ]
                  : []),
                ...(hasReadPrivilege(RESOURCES.EXTERNAL_USERS)
                  ? [
                      {
                        text: t('external_users'),
                        handleOnClick: () => navigate('/admin/external-users'),
                        pathname: '/admin/external-users',
                      },
                    ]
                  : []),
                ...(hasReadPrivilege(
                  RESOURCES.EXTERNAL_ORGANIZATIONS_CATEGORIES
                )
                  ? [
                      {
                        text: t('external_categories'),
                        handleOnClick: () => {
                          sessionStorage.removeItem('sidebarCategoriesContext');
                          navigate('/admin/categories');
                        },
                        pathname: '/admin/categories',
                      },
                    ]
                  : []),
                ...(hasReadPrivilege(RESOURCES.ORG_STRUCTURE)
                  ? [
                      {
                        text: t('organization_structure'),
                        handleOnClick: () => {
                          navigate('/admin/organization');
                        },
                        pathname: '/admin/organization',
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),

      // Form Management - RBAC
      ...(canAccessFormManagement
        ? [
            {
              text: t('form_management'),
              icon: Planner,
              menuKey: 'form_management',
              menuItems: [
                ...(hasReadPrivilege(RESOURCES.SUBFORM)
                  ? [
                      {
                        text: t('subforms'),
                        handleOnClick: () => {
                          navigate('/admin/subforms');
                        },
                        pathname: '/admin/subforms',
                      },
                    ]
                  : []),
                ...(hasReadPrivilege(RESOURCES.FORM)
                  ? [
                      {
                        text: t('forms'),
                        handleOnClick: () => {
                          navigate('/admin/forms');
                        },
                        pathname: '/admin/forms',
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),

      // Workflows - RBAC
      ...(canAccessWorkflows
        ? [
            {
              text: t('workflow_management'),
              handleOnClick: () => {
                navigate('/admin/workflows');
              },
              icon: PresentationHierarchyLight,
              pathname: '/admin/workflows',
            },
          ]
        : []),

      // Admin Requests - RBAC
      ...(canAccessAdminRequests
        ? [
            {
              text: t('requests'),
              handleOnClick: () => {
                navigate('/admin/approvals/requests');
              },
              icon: StatusCheck,
              pathname: '/admin/approvals/requests',
            },
          ]
        : []),

      // Categories - Special Logic (inserted by useSidebarCategories)
      ...categoriesMenuItems,

      // Notifications & SLA - RBAC
      ...(canAccessNotificationsSLA
        ? [
            {
              text: t('notifications_and_reminders'),
              icon: Bell,
              menuKey: 'notifications_and_reminders',
              menuItems: [
                {
                  text: t('notification_configurations'),
                  handleOnClick: () => {
                    navigate('/admin/notifications');
                  },
                  pathname: '/admin/notifications',
                },
                {
                  text: t('reminders'),
                  handleOnClick: () => {
                    navigate('/admin/reminders');
                  },
                  pathname: '/admin/reminders',
                },
              ],
            },
          ]
        : []),
      ...(checkUserRole(RESOURCES.PAYMENTS)
        ? [
            {
              text: t('payments'),
              handleOnClick: () => {
                navigate('/admin/payments');
              },
              icon: CreditCardBack,
              pathname: '/admin/payments',
            },
          ]
        : []),

      // Audit Logs - RBAC
      ...(canAccessAuditLogs
        ? [
            {
              text: t('audit_logs'),
              icon: FileAccountPlan,
              menuKey: 'audit_logs',
              menuItems: [
                ...(canAccessRoleGroupsLogs
                  ? [
                      {
                        text: t('role_groups'),
                        handleOnClick: () => {
                          navigate(`/admin/logs/${RESOURCES.ROLE_GROUPS}`);
                        },
                        pathname: `/admin/logs/${RESOURCES.ROLE_GROUPS}`,
                      },
                    ]
                  : []),
                ...(canAccessAdminUsersLogs
                  ? [
                      {
                        text: t('admin_users'),
                        handleOnClick: () => {
                          navigate(`/admin/logs/${RESOURCES.ADMIN_USERS}`);
                        },
                        pathname: `/admin/logs/${RESOURCES.ADMIN_USERS}`,
                      },
                    ]
                  : []),
                ...(canAccessExternalUsersLogs
                  ? [
                      {
                        text: t('external_users'),
                        handleOnClick: () => {
                          navigate(`/admin/logs/${RESOURCES.EXTERNAL_USERS}`);
                        },
                        pathname: `/admin/logs/${RESOURCES.EXTERNAL_USERS}`,
                      },
                    ]
                  : []),
                ...(canAccessExternalCategoriesLogs
                  ? [
                      {
                        text: t('external_categories'),
                        handleOnClick: () => {
                          navigate(
                            `/admin/logs/${RESOURCES.EXTERNAL_ORGANIZATIONS_CATEGORIES}`
                          );
                        },
                        pathname: `/admin/logs/${RESOURCES.EXTERNAL_ORGANIZATIONS_CATEGORIES}`,
                      },
                    ]
                  : []),
                ...(canAccessSubformLogs
                  ? [
                      {
                        text: t('subforms'),
                        handleOnClick: () => {
                          navigate(`/admin/logs/${RESOURCES.SUBFORM}`);
                        },
                        pathname: `/admin/logs/${RESOURCES.SUBFORM}`,
                      },
                    ]
                  : []),
                ...(canAccessFormLogs
                  ? [
                      {
                        text: t('forms'),
                        handleOnClick: () => {
                          navigate(`/admin/logs/${RESOURCES.FORM}`);
                        },
                        pathname: `/admin/logs/${RESOURCES.FORM}`,
                      },
                    ]
                  : []),
                ...(canAccessWorkflowLogs
                  ? [
                      {
                        text: t('workflows'),
                        handleOnClick: () => {
                          navigate(`/admin/logs/${RESOURCES.WORKFLOWS}`);
                        },
                        pathname: `/admin/logs/${RESOURCES.WORKFLOWS}`,
                      },
                    ]
                  : []),
                ...(canAccessNotificationsSLALogs
                  ? [
                      {
                        text: t('notifications_sla'),
                        handleOnClick: () => {
                          navigate(
                            `/admin/logs/${RESOURCES.NOTIFICATIONS_SLA}`
                          );
                        },
                        pathname: `/admin/logs/${RESOURCES.NOTIFICATIONS_SLA}`,
                      },
                    ]
                  : []),
                ...(canAccessCommentsLogs
                  ? [
                      {
                        text: t('comments'),
                        handleOnClick: () => {
                          navigate(`/admin/logs/${RESOURCES.COMMENTS}`);
                        },
                        pathname: `/admin/logs/${RESOURCES.COMMENTS}`,
                      },
                    ]
                  : []),
                ...(canAccessAdditionalDocumentsLogs
                  ? [
                      {
                        text: t('additional_documents'),
                        handleOnClick: () => {
                          navigate(
                            `/admin/logs/${RESOURCES.ADDITIONAL_DOCUMENTS}`
                          );
                        },
                        pathname: `/admin/logs/${RESOURCES.ADDITIONAL_DOCUMENTS}`,
                      },
                    ]
                  : []),
                ...(canAccessOrgStructureLogs
                  ? [
                      {
                        text: t('org_structure'),
                        handleOnClick: () => {
                          navigate(`/admin/logs/${RESOURCES.ORG_STRUCTURE}`);
                        },
                        pathname: `/admin/logs/${RESOURCES.ORG_STRUCTURE}`,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      signOutItem,
    ],
    [
      t,
      navigate,
      canAccessUserManagement,
      hasReadPrivilege,
      isSuper,
      canAccessFormManagement,
      canAccessWorkflows,
      canAccessAdminRequests,
      categoriesMenuItems,
      canAccessNotificationsSLA,
      checkUserRole,
      canAccessAuditLogs,
      canAccessRoleGroupsLogs,
      canAccessAdminUsersLogs,
      canAccessExternalUsersLogs,
      canAccessExternalCategoriesLogs,
      canAccessSubformLogs,
      canAccessFormLogs,
      canAccessWorkflowLogs,
      canAccessNotificationsSLALogs,
      canAccessCommentsLogs,
      canAccessAdditionalDocumentsLogs,
      canAccessOrgStructureLogs,
      signOutItem,
    ]
  );

  const buildCustomerMenuItems = useCallback(
    () => [
      // Dashboard - Always visible
      {
        text: t('dashboard'),
        icon: HouseIcon,
        handleOnClick: () => {
          navigate('/customer/home');
        },
        pathname: '/customer/home',
      },

      // External Requests - Special Business Logic (all external users have access)
      ...(canAccessExternalRequests
        ? [
            {
              text: t('Requests'),
              icon: PresentationHierarchyLight,
              menuKey: 'requests',
              menuItems: [
                {
                  text: t('my_requests'),
                  handleOnClick: () => {
                    navigate('/customer/requests/my-requests');
                  },
                  pathname: '/customer/requests/my-requests',
                },
                {
                  text: t('new_requests'),
                  handleOnClick: () => {
                    navigate('/customer/requests/new-requests');
                  },
                  pathname: '/customer/requests/new-requests',
                },
              ],
            },
          ]
        : []),

      // Sign Out - Always visible
      signOutItem,
    ],
    [t, navigate, canAccessExternalRequests, signOutItem]
  );

  const menuItems = useMemo(
    () => (isAdmin ? buildAdminMenuItems() : buildCustomerMenuItems()),
    [isAdmin, buildAdminMenuItems, buildCustomerMenuItems]
  );

  return {
    menuItems,
    isLoading: !ready || permissionsLoading || (!isProfileLoaded && isAdmin),
  };
};

export default useMenuItems;
